import { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import { getUserWalletBalance } from "../../../services/UserService";
import { usePartnerHasSoloUser } from "../../../hooks/usePartnerHasSoloUser";

const USER_STATUSES = [
  { value: "active",    label: "Active" },
  { value: "inactive",  label: "Inactive" },
  { value: "suspended", label: "Suspended" },
];

const EditUserModal = ({ show, onHide, currentUser, partnerTeams, partnerId, onSuccess, serverFieldErrors, onClearServerFieldError }) => {
  const [formData, setFormData] = useState({
    status: "",
    team_id: "",
    suspension_reason: "",
    suspension_start_date: "",
    suspension_end_date: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const { hasSoloUser } = usePartnerHasSoloUser(partnerId);

  useEffect(() => {
    if (currentUser) {
      setFormData({
        status: currentUser.status?.toLowerCase() ?? "",
        team_id: currentUser.team_id ?? "",
        suspension_reason: "",
        suspension_start_date: "",
        suspension_end_date: "",
      });
      setErrors({});
    }
  }, [currentUser, show]);

  // Combine backend per-field errors with client-side validation errors.
  // Client-side wins on the same field so a fresh validation message never
  // sits behind a stale server error while the user is still editing.
  const displayErrors = { ...(serverFieldErrors ?? {}), ...errors };

  const isSuspending  = formData.status === "suspended";
  const isDeactivating = formData.status === "inactive";
  const willDeactivate = isSuspending || isDeactivating;

  const { data: walletBalanceData, isFetching: loadingWalletBalance } = useQuery({
    queryKey: ["userWalletBalance", partnerId, currentUser?.id],
    queryFn: () => getUserWalletBalance(partnerId, currentUser.id).then(res => res.data),
    enabled: willDeactivate && show && !!currentUser?.id && !!partnerId,
    staleTime: 0,
  });

  const walletBalance = walletBalanceData?.balance ?? null;
  const hasWalletBalance = walletBalance && parseFloat(walletBalance) > 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (serverFieldErrors?.[name] && onClearServerFieldError) {
      onClearServerFieldError(name);
    }
  };

  const validate = () => {
    const next = {};
    if (!formData.status) next.status = "Status is required.";
    if (isSuspending) {
      if (!formData.suspension_reason?.trim()) next.suspension_reason = "Reason is required.";
      if (!formData.suspension_start_date) next.suspension_start_date = "Start date is required.";
      if (!formData.suspension_end_date) next.suspension_end_date = "End date is required.";
    }
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      status: formData.status,
      team_id: formData.team_id || null,
    };

    if (isSuspending) {
      payload.suspension_reason     = formData.suspension_reason;
      payload.suspension_start_date = formData.suspension_start_date;
      payload.suspension_end_date   = formData.suspension_end_date;
    }

    setSubmitting(true);
    try {
      await onSuccess(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit User: {currentUser?.full_name}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              name="status"
              value={formData.status}
              onChange={handleChange}
              isInvalid={!!displayErrors.status}
            >
              <option value="">Select status</option>
              {USER_STATUSES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{displayErrors.status}</Form.Control.Feedback>
          </Form.Group>

          {willDeactivate && (
            <div className="mb-3">
              {loadingWalletBalance ? (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" className="text-primary" />
                </div>
              ) : hasWalletBalance ? (
                <div className="alert alert-warning mb-0">
                  <strong>This user has a wallet balance of KES {walletBalance}.</strong>
                  <br />
                  {isSuspending
                    ? "Suspending them will automatically reverse the full balance to the partner vault and notify all super admins and accountants."
                    : "Deactivating this account will automatically reverse the full balance to the partner vault and notify all super admins and accountants."
                  }
                </div>
              ) : null}
            </div>
          )}

          {!hasSoloUser && (
            <Form.Group className="mb-3">
              <Form.Label>User Group</Form.Label>
              <Form.Select
                name="team_id"
                value={formData.team_id}
                onChange={handleChange}
              >
                <option value="">No group</option>
                {partnerTeams?.map((team) => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {isSuspending && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Suspension Reason</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="suspension_reason"
                  value={formData.suspension_reason}
                  onChange={handleChange}
                  maxLength={500}
                  isInvalid={!!displayErrors.suspension_reason}
                />
                <Form.Control.Feedback type="invalid">{displayErrors.suspension_reason}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Suspension Start Date &amp; Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="suspension_start_date"
                  value={formData.suspension_start_date}
                  onChange={handleChange}
                  isInvalid={!!displayErrors.suspension_start_date}
                />
                <Form.Control.Feedback type="invalid">{displayErrors.suspension_start_date}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Suspension End Date &amp; Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="suspension_end_date"
                  value={formData.suspension_end_date}
                  onChange={handleChange}
                  isInvalid={!!displayErrors.suspension_end_date}
                />
                <Form.Control.Feedback type="invalid">{displayErrors.suspension_end_date}</Form.Control.Feedback>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Save Changes
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditUserModal;
